import { RestError } from '@azure/core-http';
import { KnownOrderDtoStatus, OrderDto } from '@groundfloor-platform/mgmt-service-g-sdk';
import { useImmer } from 'use-immer';
import { castDraft } from 'immer';
import { useMutationRequestRetrieval } from '../../order-details.queries';
import { useWaitForUpdate } from '../../../../deliveries/details/hooks/use-wait-for-update.hook';

export interface RequestRetrievalActionProps {
  order: OrderDto;
  onAbort(): void;
  onSuccess(): void;
  onFailed(): void;
}

export interface RequestRetrievalActionState {
  error?: RestError;
  loading: boolean;
}

export interface RequestRetrievalActionHandlers {
  onCancelIntent(): void;
  onConfirmRequestRetrieval(): void;
}

const initialState: RequestRetrievalActionState = {
  loading: false
};

export function useRequestRetrievalAction({ onAbort, onSuccess, order, onFailed }: RequestRetrievalActionProps) {
  const [state, setState] = useImmer(initialState);
  const requestRetrievalMutation = useMutationRequestRetrieval(order.retailer.id, order.id);

  const onSuccessHandler = useWaitForUpdate({
    maximumWaitTime: 1000 * 10,
    onFailed,
    onSuccess,
    predicate: () => order.status === KnownOrderDtoStatus.RetrievalRequired
  });

  const handlers: RequestRetrievalActionHandlers = {
    onCancelIntent() {
      onAbort();
    },
    onConfirmRequestRetrieval() {
      setState((draft) => {
        draft.loading = true;
      });
      requestRetrievalMutation.mutate(undefined, {
        onSuccess() {
          onSuccessHandler();
        },
        onError(error) {
          setState((draft) => {
            draft.loading = false;
            draft.error = castDraft(error);
          });
        }
      });
    }
  };

  return [state, handlers] as const;
}
